import * as React from 'react';
import {
  Stack,
  IStackProps,
  IStackStyles,
  ITheme,
  MessageBarType,
  MessageBarButton,
  IStyleFunctionOrObject,
  IStyle,
  concatStyleSetsWithProps
} from '@fluentui/react';
import { MessageBannerProvider, BannerInfo, BannerType, BannerAction } from '@fluidx/office-fluid-types';
import { Locale, useStringTable } from '@fluidx/utilities';
import { getStringTable } from '../../../utilities/localizationUtilities';
import { MessageBanner, MessageBannerStyleProps, MessageBannerStyles } from './MessageBanner';

export interface FluidMessageBannerProps {
  /** Host declared banner sources */
  bannerProviders: MessageBannerProvider[];

  /** Configuration for combined banner experience, including stacking heuristics */
  stackConfiguration?: BannerStackConfiguration;

  /** Host locale */
  locale: Locale;

  /** Theme applied through customizer */
  theme?: ITheme;
}

/**
 * Configuration of the FluentUI banner stack experience.
 * See FluentUI stack control: https://developer.microsoft.com/en-us/fluentui#/controls/web/stack
 */
export interface BannerStackConfiguration {
  /**
   * Maximum number of banners that can be rendered at any given time.
   * For each stack size selection (1 OR 3 OR 6), the following banner behavior will be applied
   *
   *    1 - Only one banner slot is available and is filled with the newest, high priority banner available.
   *
   *      The following prioritization preference applies:
   *      BannerType.error > BannerType.blocked > BannerType.severeWarning > BannerType.warning > BannerType.info > BannerType.success
   *
   *    3 - Three banner slots available, prioritized in the following top-down preferred groupings of BannerType:
   *          Critical banners      - [ BannerType.error, BannerType.blocked ]
   *          Warning banners       - [ BannerType.severeWarning, BannerType.warning ]
   *          Informational banners - [ BannerType.info, BannerType.success ]
   *      Each slot is filled with the newest banner first.
   *
   *    6 - Six banner slots are available, prioritized in the following top-down preference by BannerType
   *          BannerType.error
   *          BannerType.blocked
   *          BannerType.severeWarning
   *          BannerType.warning
   *          BannerType.info
   *          BannerType.success
   *      Each slot is filled with the newest banner first.
   *
   * @default 3 - Three banner slots available
   */
  stackSize?: 1 | 3 | 6;

  /** Layout customizations of the banner stack */
  stackLayout?: Pick<IStackProps, 'grow' | 'tokens'>;

  /**
   * Custom styling for the banner stack.
   * These customizations apply at the stack level and not to individual stack items (banners).
   *
   * 2. Individual banners can be styled via a nested  property named `bannerStyles`.
   *    See MessageBannerStyles interface
   */
  stackStyles?: IStyleFunctionOrObject<FluidMessageBannerStyleProps, FluidMessageBannerStyles>;

  /**
   * Global styling for the MessageBanners rendered and managed by the banner stack.
   *
   * Note:
   *   1. These styles take lower priority relative to those defined with individual banners.
   *      See BannerInfo interface
   *
   *   2. Global customization of background color is currently not supported as that would apply the same color to all message bar types
   */
  bannerStyles?: IStyleFunctionOrObject<
    MessageBannerStyleProps,
    MessageBannerStyles & {
      // Prevent global customization of background as that would apply the same color to all message bar types!
      root?: Omit<IStyle, 'background'>;
    }
  >;
}

const defaultStackConfig: BannerStackConfiguration = {
  stackSize: 3
};

type BannerInfoMap = Map<string, BannerInfo>;
type BannerStore = Map<BannerType, BannerInfoMap>;

export interface FluidMessageBannerStyles extends IStackStyles {}

export interface FluidMessageBannerStyleProps extends Pick<FluidMessageBannerProps, 'theme'> {}

export const fluidMessageBannerStackTestId = 'FluidMessageBannerStack';

/**
 * A multi-source banner control that takes in one or more banner providers and subscribes to 'CreateBanner' and 'RemoveBanner' events.
 * Internally, it creates and customizes banners from event provided data (BannerInfo).
 * It also applies heuristics for stacking, prioritization and dismiss
 */
export const FluidMessageBanner = React.memo((props: FluidMessageBannerProps) => {
  const { bannerProviders, stackConfiguration, locale } = props;

  const bannerStore = React.useRef<BannerStore>(new Map());
  const [banners, setBanners] = React.useState<JSX.Element[]>([]);

  const stringTable = useStringTable(getStringTable, locale);

  const { stackSize, stackLayout, stackStyles, bannerStyles } = { ...defaultStackConfig, ...stackConfiguration };
  const fmbStyles = concatStyleSetsWithProps(props, stackStyles);

  React.useEffect(() => {
    // Subscribe to banner sources for signals and data to create banners
    bannerProviders.map((provider) => {
      provider.on('CreateBanner', createBannerHandler);
      provider.on('RemoveBanner', removeBannerHandler);
      // Inform the provider that the listener has been attached.
      provider.emit('BannerListenerAttached');
    });

    return () => {
      // Dispose subscriptions
      bannerProviders.map((provider) => {
        provider.removeListener('CreateBanner', createBannerHandler);
        provider.removeListener('RemoveBanner', removeBannerHandler);
      });
    };
  }, [bannerProviders]);

  const createBannerHandler = React.useCallback(
    (bannerInfo: BannerInfo) => {
      const { bannerType, bannerId } = bannerInfo;
      let bannersMap = bannerStore.current.get(bannerType);

      if (!bannersMap) {
        bannersMap = new Map();
        bannerStore.current.set(bannerType, bannersMap);
      }

      bannersMap.set(bannerId, bannerInfo);

      setBanners(getBannerStack());
    },
    [bannerStore.current]
  );

  const removeBannerHandler = React.useCallback(
    (bannerId: string) => {
      for (let bannersMap of bannerStore.current.values()) {
        if (bannersMap.has(bannerId)) {
          // Remove rendered banner with provided id
          bannersMap.delete(bannerId);
          setBanners(getBannerStack());
          return;
        }
      }
    },
    [bannerStore.current]
  );

  const getBannerStack = React.useCallback((): JSX.Element[] => {
    const bannerInfoStore = bannerStore.current;

    const errorBanner = getPreferredBannerForSlot(BannerType.error, bannerInfoStore);
    const blockedBanner = getPreferredBannerForSlot(BannerType.blocked, bannerInfoStore);
    const severeWarningBanner = getPreferredBannerForSlot(BannerType.severeWarning, bannerInfoStore);
    const warningBanner = getPreferredBannerForSlot(BannerType.warning, bannerInfoStore);
    const infoBanner = getPreferredBannerForSlot(BannerType.info, bannerInfoStore);
    const successBanner = getPreferredBannerForSlot(BannerType.success, bannerInfoStore);

    switch (stackSize) {
      case 1:
        const priorityBanner =
          errorBanner || blockedBanner || severeWarningBanner || warningBanner || infoBanner || successBanner;

        // TODO: Consider alternative prioritization of banners: like using timestamps for firstBanner or lastBanner stack order modes
        return [...(priorityBanner ? [priorityBanner] : [])];

      case 6:
        return [
          ...(errorBanner ? [errorBanner] : []),
          ...(blockedBanner ? [blockedBanner] : []),
          ...(severeWarningBanner ? [severeWarningBanner] : []),
          ...(warningBanner ? [warningBanner] : []),
          ...(infoBanner ? [infoBanner] : []),
          ...(successBanner ? [successBanner] : [])
        ];

      case 3:
      default:
        const criticalBannerSlot = errorBanner || blockedBanner;
        const warningBannerSlot = severeWarningBanner || warningBanner;
        const infoBannerSlot = infoBanner || successBanner;

        return [
          ...(criticalBannerSlot ? [criticalBannerSlot] : []),
          ...(warningBannerSlot ? [warningBannerSlot] : []),
          ...(infoBannerSlot ? [infoBannerSlot] : [])
        ];
    }
  }, [bannerStore.current]);

  const getPreferredBannerForSlot = React.useCallback(
    (bannerType: BannerType, bannerStore: BannerStore): JSX.Element | null => {
      const bannerInfosMap = bannerStore.get(bannerType);

      if (!bannerInfosMap || bannerInfosMap?.size < 1) {
        return null;
      }

      const bannerTypeDefaults = getBannerTypeDefaults(bannerType);

      // Return newest banner available
      return createBannerElement({ ...bannerTypeDefaults, ...[...bannerInfosMap.values()].reverse()[0] });
    },
    [stringTable]
  );

  const createBannerElement = React.useCallback(
    ({
      bannerId,
      bannerType,
      bannerStrings,
      actions,
      icon,
      isMultiline,
      isAssertive,
      isDismissible
    }: BannerInfo): JSX.Element => {
      const onBannerDismiss = isDismissible
        ? () => {
            removeBannerHandler(bannerId);
          }
        : undefined;
      const dismissAriaLabel = stringTable?.close;

      // Create banner element from provided info
      return (
        <Stack.Item key={bannerId}>
          <MessageBanner
            messageBarType={getMessageBarType(bannerType)}
            bannerStrings={bannerStrings}
            actions={actions?.map((action) => getBannerAction(action))}
            icon={icon}
            isMultiline={isMultiline}
            isAssertive={isAssertive}
            onDismiss={onBannerDismiss}
            dismissButtonAriaLabel={dismissAriaLabel}
            styles={bannerStyles}
          />
        </Stack.Item>
      );
    },
    [stringTable]
  );

  return (
    <Stack {...stackLayout} styles={fmbStyles} data-testid={fluidMessageBannerStackTestId}>
      {banners}
    </Stack>
  );
});

const getBannerAction = ({ actionCallback, label }: BannerAction): JSX.Element => {
  return (
    <MessageBarButton
      onClick={actionCallback}
      styles={{
        root: {
          width: 'auto'
        }
      }}
      ariaLabel={label}
      key={label}
    >
      {label}
    </MessageBarButton>
  );
};

const getMessageBarType = (bannerType: BannerType): MessageBarType => {
  switch (bannerType) {
    case BannerType.info:
      return MessageBarType.info;
    case BannerType.error:
      return MessageBarType.error;
    case BannerType.blocked:
      return MessageBarType.blocked;
    case BannerType.severeWarning:
      return MessageBarType.severeWarning;
    case BannerType.success:
      return MessageBarType.success;
    case BannerType.warning:
      return MessageBarType.warning;
  }
};

const getBannerTypeDefaults = (
  bannerType: BannerType
): { isAssertive: boolean; isDismissible: boolean; isMultiline: boolean } => {
  // TODO: Make banners single line by default, with expand / see more button
  const isMultiline = false;

  switch (bannerType) {
    case BannerType.error:
    case BannerType.blocked:
      return {
        isAssertive: true,
        isDismissible: false,
        isMultiline
      };
    case BannerType.severeWarning:
    case BannerType.warning:
      return {
        isAssertive: false,
        isDismissible: true,
        isMultiline
      };
    case BannerType.info:
    case BannerType.success:
      return {
        isAssertive: false,
        isDismissible: true,
        isMultiline
      };
  }
};

// cspell:ignore customizer testid
